import { Check, Copy, ExternalLink, Plus, X } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { type RoadmapItem, useSetRoadmapEffort } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { hoursLabel } from "@/lib/effort";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

import { DetailsCard, EditPencil, Failure, useFocusBack } from "./details-parts";
import { StepButtons } from "./step-buttons";
import {
  developerCardSave,
  type DeveloperDraft,
  developerDraft,
  MAX_LINEAR_IDS,
  normalLinearId,
  parseWholeHours,
  withLinearId,
} from "./utils";

interface DeveloperCardProps {
  item: RoadmapItem;
  editing: boolean;
  onEditing: (on: boolean) => void;
}

/**
 * Only for a developer, whose items carry `developer`: the Linear tickets as
 * links, under them the command that builds them in Claude Code (an item not
 * done), and the actual hours of a done item (an item in progress gets them
 * at Markeer klaar). The pencil makes the tickets removable chips with an add
 * field, and the hours a box.
 */
export function DeveloperCard({ item, editing, onEditing }: DeveloperCardProps) {
  const { t } = useTranslation();
  const pencil = useFocusBack(editing);
  const linear = item.developer?.linear ?? [];
  const done = item.status === "done";
  return (
    <DetailsCard
      title={t("roadmap.developer.title")}
      section="developer"
      pencil={
        item.can.set_effort &&
        !editing && (
          <EditPencil
            ref={pencil}
            label={t("roadmap.developer.edit")}
            onClick={() => onEditing(true)}
          />
        )
      }
    >
      {editing ? (
        <DeveloperForm item={item} onClose={() => onEditing(false)} />
      ) : (
        <div className="space-y-2 text-sm">
          <div>
            <p className="text-muted text-xs">{t("roadmap.developer.linear")}</p>
            {linear.length === 0 ? (
              <p className="text-muted">{t("roadmap.developer.noLinear")}</p>
            ) : (
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {linear.map((ticket) => (
                  <li key={ticket.id}>
                    <LinearChip id={ticket.id} url={ticket.url} />
                  </li>
                ))}
              </ul>
            )}
            {linear.length > 0 && !done && (
              <CopyCommand
                command={`/linear-next ${linear.map((ticket) => ticket.id).join(" ")}`}
              />
            )}
          </div>
          {(done || item.status === "in_progress") && (
            <div>
              <p className="text-muted text-xs">{t("roadmap.developer.actual")}</p>
              <p data-fact="actual">
                {done ? (
                  item.actual_hours === null || item.actual_hours === undefined ? (
                    <span className="text-muted">{t("roadmap.developer.actualMissing")}</span>
                  ) : (
                    hoursLabel(item.actual_hours)
                  )
                ) : (
                  <span className="text-muted">{t("roadmap.developer.actualAtFinish")}</span>
                )}
              </p>
            </div>
          )}
        </div>
      )}
    </DetailsCard>
  );
}

/** How long the check mark shows after a copy. */
const COPIED_MS = 2_000;

/**
 * The command that builds the item's tickets in Claude Code, as a code block
 * with a copy button at its end. A click in the code selects all of it, for
 * a browser that refuses the clipboard.
 */
function CopyCommand({ command }: { command: string }) {
  const { t } = useTranslation();
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const label = t(
    state === "copied" ? "roadmap.developer.commandCopied" : "roadmap.developer.commandCopy",
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      logger.debug("[roadmap]", "copied the build command");
      setState("copied");
      window.setTimeout(() => setState("idle"), COPIED_MS);
    } catch (err) {
      logger.warn("[roadmap] could not copy the build command", err);
      setState("failed");
    }
  };

  return (
    <div className="mt-2" data-fact="command">
      <div className="bg-accent-light flex items-center gap-1 rounded-md pl-3">
        <code className="text-foreground min-w-0 flex-1 py-1.5 font-mono text-xs wrap-anywhere select-all">
          {command}
        </code>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={label}
              onClick={() => void copy()}
              className="text-muted hover:bg-background size-8 shrink-0 pointer-coarse:size-10"
            >
              {state === "copied" ? (
                <Check aria-hidden="true" className="text-primary size-4" />
              ) : (
                <Copy aria-hidden="true" className="size-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </div>
      <p
        role="status"
        className={cn("text-xs", state === "failed" ? "text-muted mt-1.5" : "sr-only")}
      >
        {state === "copied" && t("roadmap.developer.commandCopiedStatus")}
        {state === "failed" && t("roadmap.developer.commandCopyFailed")}
      </p>
    </div>
  );
}

interface LinearChipProps {
  id: string;
  /** Its address in Linear, when the server knows it: the chip is then a link. */
  url?: string;
  /** Removes it from the form's tickets; none in read mode. */
  onRemove?: () => void;
}

/** A Linear ticket as a small chip, a link when the server knows its address. */
function LinearChip({ id, url, onRemove }: LinearChipProps) {
  const { t } = useTranslation();
  return (
    <span className="bg-accent-light text-primary inline-flex items-center gap-1 rounded-sm py-0.5 pr-0.5 pl-2 text-xs font-medium">
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          aria-label={t("roadmap.developer.linearOpen", { id })}
          className="focus-visible:ring-ring inline-flex items-center gap-1 rounded-sm hover:underline focus-visible:ring-2 focus-visible:outline-none"
        >
          {id}
          <ExternalLink aria-hidden="true" className="size-3" />
        </a>
      ) : (
        <span>{id}</span>
      )}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t("roadmap.developer.linearRemove", { id })}
          className="hover:bg-primary hover:text-primary-foreground focus-visible:ring-ring tap-target rounded-sm p-0.5 focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative"
        >
          <X aria-hidden="true" className="size-3" />
        </button>
      ) : (
        <span className="pr-1.5" />
      )}
    </span>
  );
}

interface DeveloperFormProps {
  item: RoadmapItem;
  /** Back to the card: after a save, or on cancel. */
  onClose: () => void;
}

/**
 * The developer's part in place: the tickets as removable chips with an add
 * field (an item not done), the actual hours (a done item). Save sends only
 * what the developer changed here, an id still in the add field included. A
 * field left alone follows the item, so a change saved meanwhile (the month
 * in Planning, the hours at Markeer klaar) is never sent back.
 */
function DeveloperForm({ item, onClose }: DeveloperFormProps) {
  const { t } = useTranslation();
  const ids = useId();
  const save = useSetRoadmapEffort();
  // Only what the developer changed here; the rest follows the item as it is now.
  const [edits, setEdits] = useState<Partial<DeveloperDraft>>({});
  const [linearText, setLinearText] = useState("");
  const [linearInvalid, setLinearInvalid] = useState(false);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const done = item.status === "done";
  const draft = developerDraft(item, edits);
  // An id typed but not yet added with + or Enter: Opslaan takes it along.
  const { change, problem, typedInvalid } = developerCardSave(item, draft, linearText);
  const urls = new Map((item.developer?.linear ?? []).map((ticket) => [ticket.id, ticket.url]));

  const addLinear = () => {
    const id = normalLinearId(linearText);
    if (!id) {
      setLinearInvalid(true);
      return;
    }
    setLinearInvalid(false);
    setLinearText("");
    setEdits({ ...edits, linear: withLinearId(draft.linear, id) });
  };

  const submit = async () => {
    if (save.isPending) {
      return;
    }
    // Text in the add field that is no Linear id: say so rather than drop it.
    if (typedInvalid) {
      setLinearInvalid(true);
      return;
    }
    if (!change) {
      return;
    }
    setFailure(null);
    try {
      await save.mutateAsync({ itemId: item.id, change });
      onClose();
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  const actual = parseWholeHours(draft.actual);
  return (
    <form
      className="space-y-3 text-sm"
      data-section="developer-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className="space-y-1.5">
        <p className="text-muted text-xs" id={`${ids}-linear`}>
          {t("roadmap.developer.linear")}
        </p>
        {draft.linear.length > 0 ? (
          <ul aria-labelledby={`${ids}-linear`} className="flex flex-wrap gap-1.5">
            {draft.linear.map((id) => (
              <li key={id}>
                <LinearChip
                  id={id}
                  url={urls.get(id)}
                  onRemove={
                    done
                      ? undefined
                      : () =>
                          setEdits({
                            ...edits,
                            linear: draft.linear.filter((other) => other !== id),
                          })
                  }
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">{t("roadmap.developer.noLinear")}</p>
        )}
        {!done && (
          <div className="flex items-start gap-2">
            <Input
              value={linearText}
              onChange={(event) => {
                setLinearText(event.target.value);
                setLinearInvalid(false);
              }}
              onKeyDown={(event) => {
                // Enter adds what is typed; in an empty field it saves the form, as anywhere else.
                if (event.key === "Enter" && linearText.trim() !== "") {
                  event.preventDefault();
                  addLinear();
                }
              }}
              placeholder={t("roadmap.developer.linearInput")}
              aria-label={t("roadmap.developer.linearInput")}
              aria-invalid={linearInvalid || undefined}
              autoFocus
              className="h-8 min-w-0 flex-1 pointer-coarse:h-10"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 pointer-coarse:h-10"
              onClick={addLinear}
              disabled={linearText.trim() === "" || draft.linear.length >= MAX_LINEAR_IDS}
              aria-label={t("roadmap.developer.linearAdd")}
            >
              <Plus aria-hidden="true" className="size-4" />
            </Button>
          </div>
        )}
        {linearInvalid && (
          <p role="alert" className="text-destructive text-xs">
            {t("roadmap.developer.linearInvalid")}
          </p>
        )}
      </div>
      {done && (
        <div className="space-y-1">
          <label htmlFor={`${ids}-actual`} className="text-muted block text-xs">
            {t("roadmap.developer.actual")}
          </label>
          <div className="flex items-center gap-2">
            <Input
              id={`${ids}-actual`}
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              value={draft.actual}
              onChange={(event) => setEdits({ ...edits, actual: event.target.value })}
              aria-invalid={actual === undefined || undefined}
              aria-describedby={`${ids}-actual-hint`}
              autoFocus
              className="h-8 w-24 tabular-nums pointer-coarse:h-10"
            />
            <span className="text-muted">{t("roadmap.developer.hoursUnit")}</span>
          </div>
          <p id={`${ids}-actual-hint`} className="text-muted text-xs">
            {t("roadmap.details.finishHint")}
          </p>
        </div>
      )}
      {(problem || failure) && (
        <Failure>
          {problem ? t(problem) : apiErrorMessage(failure?.error, "setRoadmapEffort")}
        </Failure>
      )}
      <StepButtons
        onBack={onClose}
        confirm={t("common.save")}
        pending={save.isPending}
        // Text in the add field that is no id keeps Opslaan on, so pressing it says why.
        disabled={!change && !typedInvalid}
      />
    </form>
  );
}
